import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SignupPage from './SignupPage';
import LoginPage from './LoginPage';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import { apiConfig, apiCall } from '../config/api';

const AuthManager = ({ onAuthSuccess }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentView, setCurrentView] = useState('login'); // 'login', 'signup', 'forgot', 'reset'
  const [resetToken, setResetToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for reset token in URL on mount
  useEffect(() => {
    const token = searchParams.get('token');
    if (token && window.location.pathname === '/reset-password') {
      setResetToken(token);
      setCurrentView('reset');
    }
  }, [searchParams]);

  const verifyToken = useCallback(async (token, user) => {
    try {
      const response = await apiCall(apiConfig.endpoints.auth.verify, {
        method: 'GET'
      });
      
      if (response.success) {
        onAuthSuccess(user);
      } else {
        // Token invalid, clear storage
        localStorage.removeItem('aminole_token');
        localStorage.removeItem('aminole_user');
        localStorage.removeItem('aminole_token_expiry');
        setIsLoading(false);
      }
    } catch (error) {
      // Token invalid, clear storage
      localStorage.removeItem('aminole_token');
      localStorage.removeItem('aminole_user');
      localStorage.removeItem('aminole_token_expiry');
      setIsLoading(false);
    }
  }, [onAuthSuccess]);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('aminole_token');
    const userStr = localStorage.getItem('aminole_user');
    const tokenExpiry = localStorage.getItem('aminole_token_expiry');
    
    if (token && userStr && tokenExpiry) {
      try {
        const user = JSON.parse(userStr);
        const expiryTime = parseInt(tokenExpiry);
        const now = Date.now();
        
        // Check if token is still valid
        if (now < expiryTime) {
          // Verify token with backend
          verifyToken(token, user);
        } else {
          // Token expired, clear storage
          localStorage.removeItem('aminole_token');
          localStorage.removeItem('aminole_user');
          localStorage.removeItem('aminole_token_expiry');
          setIsLoading(false);
        }
      } catch (error) {
        // Invalid stored data, clear it
        localStorage.removeItem('aminole_token');
        localStorage.removeItem('aminole_user');
        localStorage.removeItem('aminole_token_expiry');
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [verifyToken]);

  const handleAuthSuccess = (user) => {
    onAuthSuccess(user);
    navigate('/');
  };

  const switchToSignup = () => {
    setCurrentView('signup');
  };

  const switchToLogin = () => {
    setCurrentView('login');
    setResetToken(null);
    navigate('/auth');
  };

  const switchToForgotPassword = () => {
    setCurrentView('forgot');
  };

  const handleResetSuccess = () => {
    setResetToken(null);
    setCurrentView('login');
    navigate('/auth');
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #00bcd4 0%, #00acc1 100%)'
      }}>
        <div style={{
          color: 'white',
          fontSize: '18px',
          fontWeight: '600'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  const renderView = () => {
    switch (currentView) {
      case 'signup':
        return (
          <SignupPage 
            onSignupSuccess={handleAuthSuccess}
            onSwitchToLogin={switchToLogin}
          />
        );
      case 'forgot':
        return (
          <ForgotPassword 
            onBackToLogin={switchToLogin}
          />
        );
      case 'reset':
        return (
          <ResetPassword 
            token={resetToken}
            onBackToLogin={switchToLogin}
            onResetSuccess={handleResetSuccess}
          />
        );
      case 'login':
      default:
        return (
          <LoginPage 
            onLoginSuccess={handleAuthSuccess}
            onSwitchToSignup={switchToSignup}
            onForgotPassword={switchToForgotPassword}
          />
        );
    }
  };

  return <>{renderView()}</>;
};

export default AuthManager;
